import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { getFile } from 'src/app/shared/hooks/file';
import { shareNativeExtended } from 'src/app/shared/share/mobileNativeShare';
import { copyToClipboard } from 'src/app/shared/utils/clipboard';

import { stripQuery } from '../../utils/stripQuery';

import type { ActionsReturnType, PostSharingHookProps } from './types';

const TRACKING_EVENT_NAME = 'share_petition';
// The following is not required by product but required by the native share utility
const SHARING_EVENT_PREFIX = 'native_share_ready_to_share_instagram_post';
export function useInstagramPostSharing(props: PostSharingHookProps): ActionsReturnType {
	const track = useTracking();
	const i18n = useI18n();

	const {
		previewImageUrl,
		message,
		trackingProps: { context, viewContext },
	} = props;

	const primaryAction = useCallback(async () => {
		void copyToClipboard(message, i18n);
		const image = await getFile(stripQuery(previewImageUrl));
		const trackingData = {
			context,
			location: 'instagram_post',
			tab_at_share: viewContext,
			content: 'post',
			app_available: false,
		};

		if (!image) {
			return;
		}

		void track(TRACKING_EVENT_NAME, trackingData);
		await shareNativeExtended(
			{ files: [image] },
			{
				trackingEvents: { success: `${SHARING_EVENT_PREFIX}_success`, failure: `${SHARING_EVENT_PREFIX}_failure` },
				trackingData,
			},
			track,
		);
	}, [context, i18n, previewImageUrl, message, track, viewContext]);

	return {
		primaryAction,
		secondaryAction: undefined,
	};
}
